/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Main notifications hook - composes all domain-specific hooks
 */

"use client";

import { useFeatureFlag } from "@/components/providers/feature-flags-provider";
import { useOrganization } from "@/contexts/organization-context";
import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useUserSafe } from "@/hooks/auth/use-user-safe";
import { DEFAULT_TEST_USERS } from "@/lib/testing/test-mode-simple";
import { useCallback, useEffect, useMemo } from "react";
import type {
  NotificationAuthProvider,
  UseNotificationsOptions,
  UseNotificationsReturn,
} from "./types";
import { useNotificationsFetch } from "./use-notifications-fetch";
import { useNotificationsMutations } from "./use-notifications-mutations";
import { useNotificationsRealtime } from "./use-notifications-realtime";
import { useNotificationsState } from "./use-notifications-state";

/**
 * Core notifications hook that works with any auth provider
 */
function useNotificationsCore(
  auth: NotificationAuthProvider,
  pushNotificationsEnabled: boolean,
  options: UseNotificationsOptions = {},
): UseNotificationsReturn {
  // State management
  const {
    notifications,
    setNotifications,
    unreadCount,
    setUnreadCount,
    isLoading,
    setIsLoading,
    error,
    setError,
    realtimeStatus,
    setRealtimeStatus,
  } = useNotificationsState();

  // Memoize options with stable keys to prevent infinite re-renders
  const memoizedOptions = useMemo(
    () => ({
      limit: options.limit,
      unreadOnly: options.unreadOnly,
      type: options.type,
      mentionsOnly: options.mentionsOnly,
      startDate: options.startDate,
      endDate: options.endDate,
      search: options.search,
      realtime: options.realtime,
    }),
    [
      options.limit,
      options.unreadOnly,
      options.type,
      options.mentionsOnly,
      options.startDate,
      options.endDate,
      options.search,
      options.realtime,
    ],
  );

  // Fetching
  const { fetchNotifications } = useNotificationsFetch({
    auth,
    pushNotificationsEnabled,
    options: memoizedOptions,
    setNotifications,
    setUnreadCount,
    setIsLoading,
    setError,
  });

  // Mutations
  const { markAsRead, markAllAsRead, deleteNotification } =
    useNotificationsMutations({
      auth,
      notifications,
      setNotifications,
      setUnreadCount,
    });

  // Realtime subscription
  useNotificationsRealtime({
    enabled: memoizedOptions.realtime ?? false,
    userId: auth.userId,
    organizationId: auth.organizationId,
    pushNotificationsEnabled,
    setNotifications,
    setUnreadCount,
    setRealtimeStatus,
  });

  // Fetch notifications on mount and when options change
  useEffect(() => {
    if (!pushNotificationsEnabled || !auth.userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    fetchNotifications();
  }, [
    auth.userId,
    pushNotificationsEnabled,
    memoizedOptions.limit,
    memoizedOptions.unreadOnly,
    memoizedOptions.type,
    memoizedOptions.startDate,
    memoizedOptions.endDate,
    memoizedOptions.mentionsOnly,
    memoizedOptions.search,
    fetchNotifications,
    setNotifications,
    setUnreadCount,
  ]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications: fetchNotifications,
    realtimeStatus,
  };
}

// Re-export types
export type {
  NotificationRow,
  RealtimeStatus,
  UseNotificationsOptions,
  UseNotificationsReturn,
} from "./types";

export { useNotificationsCore };

// Test mode wrapper - for components that need to work without Clerk hooks

/**
 * Internal hook that uses Clerk hooks (via safe wrappers)
 */
function useNotificationsInternal(
  options: UseNotificationsOptions = {},
): UseNotificationsReturn {
  const { user } = useUserSafe();
  const { getToken } = useAuthSafe();
  const { organizationId } = useOrganization();
  const pushNotificationsEnabled = useFeatureFlag("push_notifications");

  return useNotificationsCore(
    {
      userId: user?.id,
      getToken,
      organizationId,
    },
    pushNotificationsEnabled,
    options,
  );
}

/**
 * Test mode hook that doesn't use Clerk hooks
 */
export function useNotificationsTestMode(
  options: UseNotificationsOptions = {},
): UseNotificationsReturn {
  const testUser = DEFAULT_TEST_USERS.owner;
  const userId = testUser.id;
  const organizationId = testUser.organizationId;
  const getToken = useCallback(async () => "test-token", []);
  const pushNotificationsEnabled = true; // Assume enabled in test mode

  return useNotificationsCore(
    {
      userId,
      getToken,
      organizationId,
    },
    pushNotificationsEnabled,
    options,
  );
}

/**
 * Public hook - always calls internal version (with Clerk hooks)
 * Test mode components should use useNotificationsTestMode directly
 */
export function useNotifications(
  options: UseNotificationsOptions = {},
): UseNotificationsReturn {
  return useNotificationsInternal(options);
}
